"use client";

import React from "react";
import { ArrowRight, ChevronLeft, ChevronRight, FileText, Settings2 } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
function CaseInsightsConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  if (loading) {
    return <div className="mx-auto max-w-4xl space-y-8">
        {[...Array(3)].map((_, i) => <Card key={i} data-index={i} className="animate-pulse border-border/50 bg-card/40">
            <CardHeader className="pb-4">
              <div className="h-6 w-2/3 rounded-md bg-muted/50"></div>
              <div className="mt-2 h-4 w-1/4 rounded-md bg-muted/50"></div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <div className="h-4 w-full rounded-md bg-muted/50"></div>
                <div className="h-4 w-5/6 rounded-md bg-muted/50"></div>
                <div className="h-4 w-4/6 rounded-md bg-muted/50"></div>
              </div>
            </CardContent>
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="mx-auto max-w-4xl rounded-xl border border-destructive/50 bg-destructive/10 p-8 text-center">
        <p className="text-destructive">Nepodařilo se načíst poznatky z případů.</p>
        <p className="mt-2 text-sm text-muted-foreground">{error}</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="mx-auto max-w-4xl rounded-xl border border-border/50 bg-card/40 p-12 text-center backdrop-blur-sm">
        <Settings2 className="mx-auto mb-4 size-12 text-muted-foreground opacity-50" />
        <h3 className="text-xl font-semibold text-foreground">Žádné poznatky nejsou k dispozici</h3>
        <p className="mt-2 text-muted-foreground">Vraťte se později pro nové inženýrské případové studie.</p>
      </div>;
  }
  return <div className="mx-auto max-w-4xl">
      <div className="space-y-8">
        {posts.map((post, i) => <Card key={post.id} data-index={i} className="group overflow-hidden border-border/50 bg-card/40 backdrop-blur-md transition-all duration-300 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5">
            <CardHeader className="pb-4">
              <div className="mb-3 flex flex-wrap items-center gap-3 text-xs font-medium text-muted-foreground">
                {post.date && <time dateTime={post.date} className="flex items-center gap-1.5 rounded-full bg-muted/50 px-3 py-1">
                    {new Date(post.date).toLocaleDateString('cs-CZ', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
                  </time>}
                {post.categories && post.categories.length > 0 && <span className="flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-primary">
                    {post.categories[0].name}
                  </span>}
              </div>
              <CardTitle className="text-2xl font-bold leading-tight text-foreground md:text-3xl">
                {post.title}
              </CardTitle>
            </CardHeader>
            
            <CardContent>
              <div className="relative rounded-lg border border-border/30 bg-background/50 p-6">
                <div className="absolute left-0 top-0 h-full w-1 rounded-l-lg bg-secondary"></div>
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-secondary">
                  <FileText className="size-4" />
                  <span>Technický přehled</span>
                </div>
                <div className="prose prose-sm prose-invert max-w-none text-muted-foreground" dangerouslySetInnerHTML={{
              __html: post.excerpt || "No technical brief available for this case."
            }} />
              </div>
              
              <div className="mt-6 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {post.author?.avatar && post.author.avatar["48"] ? <img src={post.author.avatar["48"]} alt={post.author.name} className="size-8 rounded-full border border-border/50" /> : <div className="flex size-8 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground">
                      {post.author?.name?.charAt(0) || "N"}
                    </div>}
                  <span className="text-sm font-medium text-foreground">
                    {post.author?.name || "NEXO Engineering Team"}
                  </span>
                </div>
                
                {post.link && <Link to={post.link}>
                    <Button variant="ghost" className="group/btn text-primary hover:bg-primary/10 hover:text-primary">
                      Přečíst celou kazuistiku
                      <ArrowRight className="ml-2 size-4 transition-transform group-hover/btn:translate-x-1" />
                    </Button>
                  </Link>}
              </div>
            </CardContent>
          </Card>)}
      </div>

      {(hasNext || hasPrev) && <div className="mt-12 flex items-center justify-between rounded-xl border border-border/50 bg-card/20 p-4 backdrop-blur-sm">
          <Button variant="outline" onClick={prevPage} disabled={!hasPrev} className="border-border/50 bg-background/50 hover:bg-accent hover:text-accent-foreground">
            <ChevronLeft className="mr-2 size-4" />
            Předchozí
          </Button>
          <span className="text-sm font-medium text-muted-foreground">
            Strana {currentPage} z {totalPages}
          </span>
          <Button variant="outline" onClick={nextPage} disabled={!hasNext} className="border-border/50 bg-background/50 hover:bg-accent hover:text-accent-foreground">
            Další
            <ChevronRight className="ml-2 size-4" />
          </Button>
        </div>}
    </div>;
}
export default function CaseInsights() {
  const wp_query_params = {
    per_page: 3,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query("post", wp_query_params);
  return <section id="case-insights" className="dark bg-background text-foreground relative py-24">
      <div className="pointer-events-none absolute inset-0 opacity-[0.02]" style={{
      backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)',
      backgroundSize: '32px 32px'
    }}></div>
      
      <div className="relative mx-auto max-w-screen-xl px-4 md:px-6">
        <div className="mb-16 text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5 text-sm font-semibold text-primary">
            <Settings2 className="size-4" />
            <span>Inženýrský deník</span>
          </div>
          <h2 className="text-4xl font-extrabold tracking-tight text-foreground md:text-5xl">
            Poznatky z praxe
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
            Hloubkové analýzy složitých výzev v oblasti HVAC, diagnostických procesů a technických řešení implementovaných naším inženýrským týmem.
          </p>
        </div>

        <PostsProvider wp_query={wp_query}>
          <CaseInsightsConsumer />
        </PostsProvider>
      </div>
    </section>;
}